import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { MY_ACADEMIC_HEALTH, MY_BACKLOGS, ADD_BACKLOG, CLEAR_BACKLOG } from '../graphql';
import { Activity, AlertTriangle, CheckCircle, XCircle, TrendingUp, Plus, Trash2 } from 'lucide-react';

const STATUS_CONFIG = {
  SAFE:     { color: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200', icon: CheckCircle, bar: 'bg-green-500' },
  WARNING:  { color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200',icon: AlertTriangle,bar:'bg-yellow-500' },
  DANGER:   { color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200',icon: AlertTriangle,bar:'bg-orange-500' },
  DETAINED: { color: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-300',   icon: XCircle,     bar: 'bg-red-500' },
};

export default function AcademicHealthPage() {
  const { data, loading } = useQuery(MY_ACADEMIC_HEALTH);
  const { data: backlogData, refetch: refetchBacklogs } = useQuery(MY_BACKLOGS);
  const [addBacklog] = useMutation(ADD_BACKLOG, { refetchQueries: [MY_BACKLOGS, MY_ACADEMIC_HEALTH] });
  const [clearBacklog] = useMutation(CLEAR_BACKLOG, { refetchQueries: [MY_BACKLOGS, MY_ACADEMIC_HEALTH] });
  const [backlogForm, setBacklogForm] = useState({ subject: '', semester: '' });
  const [showBacklogForm, setShowBacklogForm] = useState(false);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;

  const health = data?.myAcademicHealth;
  const backlogs = backlogData?.myBacklogs || [];

  const overallConfig = {
    GOOD:     { label: 'All Good',  color: 'text-green-700',  bg: 'bg-green-100', icon: CheckCircle },
    WARNING:  { label: 'Warning',   color: 'text-yellow-700', bg: 'bg-yellow-100',icon: AlertTriangle },
    CRITICAL: { label: 'Critical',  color: 'text-red-700',    bg: 'bg-red-100',   icon: XCircle },
  }[health?.overallStatus || 'GOOD'];
  const OverallIcon = overallConfig.icon;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Academic Health</h1>
        <p className="text-gray-500 text-sm mt-1">Your real academic picture — attendance risk, CGPA, backlogs, eligibility</p>
      </div>

      {/* Overall status banner */}
      {health && (
        <div className={`${overallConfig.bg} rounded-2xl p-5 mb-6`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <OverallIcon size={22} className={overallConfig.color} />
                <span className={`text-lg font-bold ${overallConfig.color}`}>{overallConfig.label}</span>
              </div>
              <div className="flex gap-6 text-sm">
                <span className={overallConfig.color}><strong>{health.cgpa > 0 ? health.cgpa.toFixed(2) : '—'}</strong> CGPA</span>
                <span className={overallConfig.color}><strong>{health.activeBacklogs}</strong> Active Backlogs</span>
                <span className={overallConfig.color}><strong>{health.detentionRisk.length}</strong> Detention Risk</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {health?.alerts?.length > 0 && (
        <div className="card mb-6 space-y-2">
          <h3 className="font-semibold text-gray-900 mb-3">Action Required</h3>
          {health.alerts.map((alert, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-gray-700 bg-yellow-50 px-3 py-2 rounded-xl">
              <AlertTriangle size={14} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              {alert}
            </div>
          ))}
        </div>
      )}

      {/* Subject health */}
      <div className="card mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Subject-wise Attendance Health</h3>
        {health?.subjectHealth?.length === 0 && (
          <p className="text-sm text-gray-400">No subjects tracked yet. Add subjects in the Attendance tab.</p>
        )}
        <div className="space-y-4">
          {health?.subjectHealth?.map(({ subject: s, status, alertMessage }) => {
            const cfg = STATUS_CONFIG[status];
            const Icon = cfg.icon;
            return (
              <div key={s.id} className={`${cfg.bg} ${cfg.border} border rounded-xl p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className={cfg.color} />
                    <span className="font-medium text-gray-900">{s.name}</span>
                  </div>
                  <span className={`font-bold text-lg ${cfg.color}`}>{s.percentage.toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-white rounded-full mb-2">
                  <div className={`h-2 ${cfg.bar} rounded-full`} style={{ width: `${Math.min(100, s.percentage)}%` }} />
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{s.attendedClasses}/{s.totalClasses} classes</span>
                  {alertMessage && <span className={cfg.color}>{alertMessage}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Backlogs */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Backlogs</h3>
          <button className="btn-secondary flex items-center gap-1 !py-1.5 !px-3 text-sm" onClick={() => setShowBacklogForm(!showBacklogForm)}>
            <Plus size={15} /> Add Backlog
          </button>
        </div>

        {showBacklogForm && (
          <form onSubmit={async e => { e.preventDefault(); await addBacklog({ variables: { subject: backlogForm.subject, semester: parseInt(backlogForm.semester) } }); setBacklogForm({ subject: '', semester: '' }); setShowBacklogForm(false); }} className="bg-gray-50 rounded-xl p-4 mb-4 flex gap-3">
            <input className="input flex-1 !py-2 text-sm" placeholder="Subject name" required value={backlogForm.subject} onChange={e=>setBacklogForm({...backlogForm,subject:e.target.value})} />
            <input className="input w-24 !py-2 text-sm" type="number" placeholder="Sem" min="1" max="8" required value={backlogForm.semester} onChange={e=>setBacklogForm({...backlogForm,semester:e.target.value})} />
            <button className="btn-primary text-sm !py-2" type="submit">Add</button>
          </form>
        )}

        {backlogs.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No active backlogs.</p>
        ) : (
          <div className="space-y-2">
            {backlogs.map(b => (
              <div key={b.id} className={`flex items-center justify-between px-4 py-3 rounded-xl ${b.cleared ? 'bg-green-50' : 'bg-red-50'}`}>
                <div>
                  <span className={`font-medium text-sm ${b.cleared ? 'line-through text-gray-400' : 'text-gray-900'}`}>{b.subject}</span>
                  <span className="text-xs text-gray-400 ml-2">Sem {b.semester}</span>
                </div>
                {!b.cleared && (
                  <button onClick={() => clearBacklog({ variables: { backlogId: b.id } })}
                    className="text-xs font-medium text-green-600 hover:text-green-700 flex items-center gap-1 px-2 py-1 bg-white rounded-lg border border-green-200">
                    <CheckCircle size={13} /> Mark Cleared
                  </button>
                )}
                {b.cleared && <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle size={13} /> Cleared</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
